import { useEffect, useState } from "react";
import { useShallow } from "zustand/react/shallow";
import { ArrowRight, History } from "lucide-react";
import Button from "@/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import useGlobalStore from "@/store/globalStore";
import useVersionUpdatesStore, {
  type VersionChoice,
  type VersionUpdate,
  versionUpdateKey,
} from "../store/useVersionUpdatesStore";

interface VersionUpdatesPanelProps {
  campaignId: string;
  /** Open the dialog straight away (arrived from a notification). */
  openOnLoad?: boolean;
  onDialogClosed?: () => void;
}

/**
 * Banner + dialog for ad-category updates this campaign has not answered yet.
 *
 * The campaign keeps showing the version it started with until someone chooses,
 * per update, to keep it or move to the newer version. Renders nothing when
 * there is nothing to answer.
 */
const VersionUpdatesPanel = ({
  campaignId,
  openOnLoad = false,
  onDialogClosed,
}: VersionUpdatesPanelProps) => {
  const { updates, canDecide, decidingKey, fetchVersionUpdates, decide } =
    useVersionUpdatesStore(
      useShallow((state) => ({
        updates: state.updates,
        canDecide: state.canDecide,
        decidingKey: state.decidingKey,
        fetchVersionUpdates: state.fetchVersionUpdates,
        decide: state.decide,
      })),
    );
  const { showSuccess, showError } = useGlobalStore(
    useShallow((state) => ({
      showSuccess: state.showSuccess,
      showError: state.showError,
    })),
  );
  const [open, setOpen] = useState(false);

  useEffect(() => {
    fetchVersionUpdates(campaignId)
      .then(() => {
        if (openOnLoad) setOpen(true);
      })
      .catch(() => {
        // Not being able to list updates must not break the dashboard; the
        // banner simply stays hidden.
      });
  }, [campaignId, openOnLoad, fetchVersionUpdates]);

  const close = () => {
    setOpen(false);
    onDialogClosed?.();
  };

  const handleDecide = async (update: VersionUpdate, choice: VersionChoice) => {
    try {
      await decide(campaignId, update, choice);
      showSuccess(
        choice === "adopt"
          ? `${update.label} updated to version ${update.to_version}.`
          : `${update.label} kept at version ${update.from_version}.`,
      );
      if (useVersionUpdatesStore.getState().updates.length === 0) close();
    } catch (error) {
      showError(
        error instanceof Error ? error.message : "Failed to save your choice",
      );
    }
  };

  if (updates.length === 0) return null;

  const count = updates.length;

  return (
    <>
      <div className="mb-6 flex items-center justify-between gap-4 rounded-lg border border-[#EA33C0]/30 bg-pink-50 px-5 py-4">
        <div className="flex items-center gap-3">
          <History size={20} className="shrink-0 text-[#EA33C0]" />
          <div>
            <p className="text-sm font-semibold text-gray-900">
              {count === 1
                ? "1 ad category update is waiting for a decision"
                : `${count} ad category updates are waiting for a decision`}
            </p>
            <p className="text-sm text-gray-600">
              This campaign keeps using its current version until you choose.
            </p>
          </div>
        </div>
        <Button type="primary" onClick={() => setOpen(true)}>
          Review updates
        </Button>
      </div>

      <Dialog open={open} onOpenChange={(next) => (next ? setOpen(true) : close())}>
        <DialogContent className="max-h-[85vh] overflow-y-auto bg-white sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Ad category updates</DialogTitle>
            <DialogDescription>
              {canDecide
                ? "Choose, for each change, whether this campaign keeps its current version or moves to the new one."
                : "Only people with permission to choose ad category versions (usually a Media Admin) can decide these."}
            </DialogDescription>
          </DialogHeader>

          <ul className="flex flex-col gap-4">
            {updates.map((update) => {
              const key = versionUpdateKey(update);
              const busy = decidingKey === key;

              return (
                <li key={key} className="rounded-lg border border-gray-200 p-4">
                  <div className="mb-3 flex items-start justify-between gap-3">
                    <div>
                      <p className="font-semibold text-gray-900">{update.label}</p>
                      {update.context && (
                        <p className="text-xs text-gray-500">{update.context}</p>
                      )}
                    </div>
                    <span className="shrink-0 rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-700">
                      v{update.from_version} → v{update.to_version}
                    </span>
                  </div>

                  {update.changes.length > 0 ? (
                    <table className="mb-4 w-full text-sm">
                      <thead>
                        <tr className="text-left text-xs text-gray-500">
                          <th className="pb-1 font-medium">Field</th>
                          <th className="pb-1 font-medium">Current (v{update.from_version})</th>
                          <th className="pb-1" />
                          <th className="pb-1 font-medium">New (v{update.to_version})</th>
                        </tr>
                      </thead>
                      <tbody>
                        {update.changes.map((change) => (
                          <tr key={change.field} className="border-t border-gray-100">
                            <td className="py-1.5 pr-3 text-gray-600">{change.field}</td>
                            <td className="py-1.5 pr-2 text-gray-900">{change.from || "—"}</td>
                            <td className="py-1.5 pr-2 text-gray-400">
                              <ArrowRight size={14} />
                            </td>
                            <td className="py-1.5 font-medium text-gray-900">{change.to || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p className="mb-4 text-sm text-gray-600">
                      {update.archived
                        ? "This item was archived in the ad category."
                        : "Details of this change are not available."}
                    </p>
                  )}

                  {canDecide && (
                    <div className="flex justify-end gap-2">
                      <Button
                        type="secondary"
                        disabled={busy}
                        onClick={() => handleDecide(update, "keep")}
                      >
                        Keep version {update.from_version}
                      </Button>
                      <Button
                        type="primary"
                        disabled={busy}
                        isLoading={busy}
                        onClick={() => handleDecide(update, "adopt")}
                      >
                        Use version {update.to_version}
                      </Button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default VersionUpdatesPanel;
